import { parseMidi, type MidiEvent } from 'midi-file'

const DEFAULT_MICROSEC_PER_BEAT = 500_000

type ScheduledNote = {
  startSec: number
  endSec: number
  midiNote: number
  velocity: number
}

function midiNoteToHz(note: number): number {
  return 440 * Math.pow(2, (note - 69) / 12)
}

function buildSchedule(bytes: Uint8Array): {
  notes: ScheduledNote[]
  durationSec: number
} {
  const midi = parseMidi(bytes)
  const ticksPerBeat = midi.header.ticksPerBeat ?? 480

  type Stamped = { tick: number; trackIdx: number; eventIdx: number; ev: MidiEvent }
  const stamped: Stamped[] = []
  midi.tracks.forEach((track, trackIdx) => {
    let t = 0
    track.forEach((ev, eventIdx) => {
      t += ev.deltaTime
      stamped.push({ tick: t, trackIdx, eventIdx, ev })
    })
  })
  stamped.sort(
    (a, b) =>
      a.tick - b.tick ||
      a.trackIdx - b.trackIdx ||
      a.eventIdx - b.eventIdx,
  )

  let curTick = 0
  let curSec = 0
  let microsecPerBeat = DEFAULT_MICROSEC_PER_BEAT

  const active = new Map<string, { startSec: number; velocity: number; midiNote: number }>()
  const notes: ScheduledNote[] = []

  for (const { tick, ev } of stamped) {
    const dTicks = tick - curTick
    curSec += (dTicks / ticksPerBeat) * (microsecPerBeat / 1_000_000)
    curTick = tick

    if (ev.type === 'setTempo') {
      microsecPerBeat = ev.microsecondsPerBeat
      continue
    }
    if (ev.type !== 'noteOn' && ev.type !== 'noteOff') continue

    const isOn = ev.type === 'noteOn' && ev.velocity > 0
    const key = `${ev.channel}-${ev.noteNumber}`

    if (isOn) {
      const prev = active.get(key)
      if (prev) {
        notes.push({ startSec: prev.startSec, endSec: curSec, midiNote: prev.midiNote, velocity: prev.velocity })
      }
      active.set(key, {
        startSec: curSec,
        velocity: ev.velocity,
        midiNote: ev.noteNumber,
      })
    } else {
      const prev = active.get(key)
      if (prev) {
        notes.push({ startSec: prev.startSec, endSec: curSec, midiNote: prev.midiNote, velocity: prev.velocity })
        active.delete(key)
      }
    }
  }
  for (const a of active.values()) {
    notes.push({ startSec: a.startSec, endSec: curSec, midiNote: a.midiNote, velocity: a.velocity })
  }

  return { notes, durationSec: curSec }
}

export type MidiPlayerCallbacks = {
  onEnded: () => void
}

export class MidiPlayer {
  private ctx: AudioContext | null = null
  private master: GainNode | null = null
  private oscillators: OscillatorNode[] = []
  private startCtxTime = 0
  private rafId: number | null = null
  private notes: ScheduledNote[]
  private durationSec: number
  private callbacks: MidiPlayerCallbacks
  private state: 'idle' | 'playing' | 'paused' | 'ended' = 'idle'

  constructor(bytes: Uint8Array, callbacks: MidiPlayerCallbacks) {
    const { notes, durationSec } = buildSchedule(bytes)
    this.notes = notes
    this.durationSec = durationSec
    this.callbacks = callbacks
  }

  async play(): Promise<void> {
    if (this.state === 'playing' || this.state === 'ended') return

    if (!this.ctx) {
      const Ctor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      this.ctx = new Ctor()
      this.master = this.ctx.createGain()
      this.master.gain.value = 0.6
      this.master.connect(this.ctx.destination)
    }

    if (this.state === 'paused') {
      await this.ctx.resume()
      this.state = 'playing'
      this.scheduleEndCheck()
      return
    }

    this.startCtxTime = this.ctx.currentTime + 0.05
    for (const n of this.notes) {
      const osc = this.ctx.createOscillator()
      const gain = this.ctx.createGain()
      osc.type = 'triangle'
      osc.frequency.value = midiNoteToHz(n.midiNote)
      const peak = (n.velocity / 127) * 0.15
      const startT = this.startCtxTime + n.startSec
      const endT = this.startCtxTime + Math.max(n.endSec, n.startSec + 0.05)
      gain.gain.setValueAtTime(0, startT)
      gain.gain.linearRampToValueAtTime(peak, startT + 0.005)
      gain.gain.setValueAtTime(peak, endT)
      gain.gain.linearRampToValueAtTime(0, endT + 0.05)
      osc.connect(gain)
      gain.connect(this.master!)
      osc.start(startT)
      osc.stop(endT + 0.06)
      this.oscillators.push(osc)
    }
    this.state = 'playing'
    this.scheduleEndCheck()
  }

  async pause(): Promise<void> {
    if (this.state !== 'playing' || !this.ctx) return
    await this.ctx.suspend()
    this.state = 'paused'
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
  }

  dispose(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
    for (const o of this.oscillators) {
      try { o.stop() } catch { /* already stopped */ }
    }
    this.oscillators = []
    if (this.ctx) {
      this.ctx.close().catch(() => {})
      this.ctx = null
    }
    this.master = null
    this.state = 'ended'
  }

  private scheduleEndCheck(): void {
    const tick = () => {
      if (!this.ctx || this.state !== 'playing') return
      if (this.ctx.currentTime - this.startCtxTime >= this.durationSec) {
        this.state = 'ended'
        this.callbacks.onEnded()
        this.dispose()
        return
      }
      this.rafId = requestAnimationFrame(tick)
    }
    this.rafId = requestAnimationFrame(tick)
  }
}
